import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Star,
  ChevronRight,
} from "lucide-react";
import { motion } from "framer-motion";
import { productService } from "../../services/productService";
import { CATEGORIES } from "../../constants/categories";
import ProductCard from "../components/ProductCard";

import bgImage2 from "../../assets/images/beauty1.webp";
import bgImage3 from "../../assets/images/beauty2.webp";

// Emojis des liens catégories (clé = valeur Firestore)
const CATEGORY_EMOJI = {
  Beauty: "✨",
  Electronique: "⚡",
  Sports: "🏃",
  Coffrets: "🎁",
  Vêtements: "👕",
  Alimentation: "🥗",
  "Hygiène & Désinfection": "🧴",
};

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay },
});


// ── Page d'accueil ─────────────────────────────────────────────
export default function Home() {
  const [productsByCategory, setProductsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [toggle, setToggle] = useState(false);

  // Alterner les images du hero
  useEffect(() => {
    const interval = setInterval(() => setToggle((prev) => !prev), 2000);
    return () => clearInterval(interval);
  }, []);

  // Charger les produits depuis Firestore : 1 produit par catégorie
  useEffect(() => {
  let cancelled = false;

  const fetchProducts = async () => {
    try {
      const results = await Promise.all(
        CATEGORIES.map((cat) =>
          productService.getOneByCategory(cat.value).catch(() => null)
        )
      );

      const grouped = {};
      CATEGORIES.forEach((cat, i) => {
        if (results[i]) grouped[cat.value] = results[i];
      });

      if (!cancelled) setProductsByCategory(grouped);
    } finally {
      if (!cancelled) setLoading(false);
    }
  };

  fetchProducts();
  return () => {
    cancelled = true;
  };
}, []);

  const displayedProducts = CATEGORIES.map((cat) => productsByCategory[cat.value]).filter(Boolean);

  return (
    <main className="min-h-screen overflow-hidden bg-white text-stone-900">
      {/* HERO */}
      <section className="relative isolate flex min-h-[650px] items-center overflow-hidden bg-stone-950 px-4 py-24 sm:px-6 lg:min-h-[750px] lg:px-8">
        <motion.div
          aria-hidden="true"
          className="absolute inset-0 -z-20 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${toggle ? bgImage2 : bgImage3})` }}
          initial={{ scale: 1 }}
          animate={{ scale: 1.15 }}
          transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
        />

        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-black/85 via-black/60 to-black/25" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/50 via-transparent to-black/10" />

        <div className="mx-auto w-full max-w-7xl">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp(0)} className="max-w-2xl text-white">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-black/30 px-4 py-2 text-sm font-medium text-amber-200 backdrop-blur-md">
                <Sparkles size={16} />
                New Collection 2026
              </div>

              <h1 className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-7xl">
                Elevate Your
                <br />
                <span className="bg-gradient-to-r from-amber-200 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
                  Lifestyle
                </span>{" "}
                With
                <br />
                Splendrofiy
              </h1>

              <p className="mt-6 max-w-xl text-base leading-7 text-white/80 sm:text-lg sm:leading-8">
                Découvrez une sélection soignée d'électronique, beauté, sport
                et bien plus encore.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/category/AllProducts"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-7 py-3.5 font-bold text-stone-950 shadow-lg transition hover:-translate-y-1 hover:bg-amber-400"
                >
                  Shop Now
                  <ArrowRight size={18} />
                </Link>

                <Link
                  to="/category/AllProducts"
                  className="inline-flex items-center justify-center rounded-xl border border-white/50 bg-white/10 px-7 py-3.5 font-semibold text-white backdrop-blur-md transition hover:bg-white hover:text-stone-900"
                >
                  Lookbook
                </Link>
              </div>
            </motion.div>

            <motion.div {...fadeUp(0.3)} className="grid grid-cols-2 gap-3 sm:gap-4">
              {[
                { val: "2400+", label: "Produits" },
                { val: "98%", label: "Satisfaction" },
                { val: "120k", label: "Clients" },
                { val: "4.9", label: "Note moy." },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-2xl border border-white/20 bg-black/30 p-5 text-center text-white shadow-lg backdrop-blur-xl transition hover:border-amber-300/60 hover:bg-black/40 sm:p-7"
                >
                  <div className="text-3xl font-extrabold text-amber-300 sm:text-4xl">
                    {s.val}
                  </div>
                  <div className="mt-2 text-sm text-white/75 sm:text-base">
                    {s.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        <div className="pointer-events-none absolute -bottom-32 -left-20 -z-10 h-72 w-72 rounded-full bg-amber-500/20 blur-3xl" />
      </section>

      {/* CATÉGORIES */}
      <section className="border-b border-stone-100 bg-white px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-wrap justify-center gap-3 sm:gap-4">
          {CATEGORIES.map((cat) => (
            <Link
              key={cat.value}
              to={`/category/${encodeURIComponent(cat.value)}`}
              className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 shadow-sm transition hover:border-amber-400 hover:bg-amber-50 hover:text-amber-800"
            >
              <span>{CATEGORY_EMOJI[cat.value] || "•"}</span>
              {cat.label}
            </Link>
          ))}
        </div>
      </section>

      {/* PRODUITS PAR CATÉGORIE */}
      <section className="bg-stone-50 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <motion.div {...fadeUp(0)} className="mb-8 flex items-end justify-between gap-4">
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-700">
                Notre sélection
              </p>
              <h2 className="text-3xl font-extrabold tracking-tight text-stone-900 sm:text-4xl">
                Best Sellers
              </h2>
              <p className="mt-2 text-sm text-stone-500 sm:text-base">
                Découvrez nos produits sélectionnés pour vous.
              </p>
            </div>

            <Link
              to="/category/AllProducts"
              className="inline-flex shrink-0 items-center gap-1 rounded-lg px-3 py-2 text-sm font-bold text-stone-700 transition hover:bg-white hover:text-amber-700"
            >
              Tout voir
              <ChevronRight size={18} />
            </Link>
          </motion.div>

          {loading ? (
            <div className="flex min-h-64 flex-col items-center justify-center">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-stone-200 border-t-amber-600" />
              <p className="mt-4 text-sm text-stone-500">Chargement des produits…</p>
            </div>
          ) : displayedProducts.length === 0 ? (
            <p className="py-16 text-center text-stone-500">Aucun produit disponible.</p>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {displayedProducts.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="min-w-0"
                >
                  <ProductCard p={p} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* AVANTAGES */}
      <section className="bg-white px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-5 md:grid-cols-3">
          {[
            { icon: Truck, title: "Livraison gratuite", desc: "Pour toute commande > 500 MAD" },
            { icon: ShieldCheck, title: "Paiement sécurisé", desc: "Transaction 100% protégée" },
            { icon: Star, title: "Qualité garantie", desc: "Produits soigneusement sélectionnés" },
          ].map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg sm:p-6"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-800">
                  <Icon size={23} />
                </div>
                <div>
                  <h3 className="font-bold text-stone-900">{f.title}</h3>
                  <p className="mt-1 text-sm text-stone-500">{f.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* BANNER CTA */}
      <section className="px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl bg-stone-950 px-6 py-14 text-center shadow-xl sm:px-12 sm:py-20">
          <div className="pointer-events-none absolute -left-20 -top-24 h-72 w-72 rounded-full bg-amber-500/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -right-20 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl" />

          <motion.div {...fadeUp(0)} className="relative z-10">
            <p className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-amber-300">
              Splendrofiy
            </p>
            <h2 className="mx-auto max-w-3xl text-3xl font-extrabold leading-tight text-white sm:text-4xl lg:text-5xl">
              Prêt à commencer votre shopping ?
            </h2>
            <p className="mt-4 text-base text-white/70 sm:text-lg">
              Rejoignez plus de 120 000 clients satisfaits
            </p>
            <Link
              to="/category/AllProducts"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-7 py-3.5 font-bold text-stone-950 transition hover:-translate-y-1 hover:bg-amber-400"
            >
              Découvrir tous les produits
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}